import { Link } from "react-router-dom";
import { GitCompare, ArrowRight } from "lucide-react";
import { COMPARISON_PILLAR_META, pillarHref } from "../data/pillarComparisonData";

/**
 * <PillarComparison>
 *
 * Wave 2 · Batch 2Q (Feb-2026) — Head-to-head "X vs Y vs Z" comparison matrix
 * rendered on every /courses/<slug> pillar that has a natural competitor set.
 * Captures single-highest-intent search queries like:
 *   • "CBSE vs ICSE vs IB"
 *   • "IB vs A-Level vs AP for US admissions"
 *   • "NEET vs JEE"
 *
 * Contract
 * --------
 *   currentSlug – slug of the pillar the user is currently on (highlighted).
 *   group       – object from getPillarComparison(slug), shape:
 *                 { columns: string[], rows: {label, cells: {slug: string}}[],
 *                   headline: string, sub: string }
 *
 * Rendering rules
 * ---------------
 *   • Uses a semantic <table> with <caption>, <thead>, <tbody> so search
 *     crawlers and screen-readers parse it as a data table (not divs).
 *   • The CURRENT-pillar column is amber-highlighted in the header row
 *     with an "You are here" badge — the user's eye lands on their pillar.
 *   • Every OTHER pillar column header is a live <Link> to that pillar's
 *     page, so the widget also serves as a topic-cluster cross-link block.
 *   • Mobile: stacks each pillar as an accordion-style card (no horizontal
 *     scroll bar, better on narrow screens).
 *   • Fully additive — no schema.org type is emitted (there is no valid
 *     schema.org for comparison tables); semantic HTML is enough for
 *     Google to parse this into "table" snippets in AI Overviews.
 */
export default function PillarComparison({ currentSlug, group }) {
    if (!group || !currentSlug) return null;
    const { columns, rows, headline, sub } = group;

    return (
        <section
            className="py-16 sm:py-20 bg-white border-t border-slate-100"
            aria-labelledby={`compare-${currentSlug}-heading`}
            data-testid={`pillar-comparison-${currentSlug}`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#1E3A8A]/5 border border-[#1E3A8A]/10 text-[10px] font-bold uppercase tracking-[0.22em] text-[#1E3A8A] mb-3">
                    <GitCompare className="w-3 h-3" /> Side-by-side comparison
                </div>
                <h2
                    id={`compare-${currentSlug}-heading`}
                    className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-3"
                >
                    {headline}
                </h2>
                <p className="text-slate-600 max-w-3xl mb-8 leading-relaxed">
                    {sub}
                </p>

                {/* -------------------------------------------------------- */}
                {/* Desktop / tablet: proper semantic <table>                */}
                {/* -------------------------------------------------------- */}
                <div className="hidden md:block overflow-x-auto -mx-2 px-2">
                    <table
                        className="w-full border-collapse text-left"
                        data-speakable="true"
                    >
                        <caption className="sr-only">{headline}</caption>
                        <thead>
                            <tr>
                                <th
                                    scope="col"
                                    className="p-4 border-b-2 border-slate-200 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 w-1/5 min-w-[160px] align-bottom"
                                >
                                    Dimension
                                </th>
                                {columns.map((slug) => {
                                    const meta = COMPARISON_PILLAR_META[slug];
                                    const isCurrent = slug === currentSlug;
                                    return (
                                        <th
                                            scope="col"
                                            key={slug}
                                            className={
                                                isCurrent
                                                    ? "p-4 border-b-2 border-amber-400 bg-amber-50 align-bottom"
                                                    : "p-4 border-b-2 border-slate-200 align-bottom"
                                            }
                                        >
                                            <div className="flex flex-col gap-1">
                                                {isCurrent ? (
                                                    <span className="inline-block px-2 py-0.5 rounded-full bg-amber-500 text-[9px] font-bold uppercase tracking-[0.18em] text-white w-fit">
                                                        You are here
                                                    </span>
                                                ) : (
                                                    <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">
                                                        Compare
                                                    </span>
                                                )}
                                                {isCurrent ? (
                                                    <span
                                                        className="font-heading text-base sm:text-lg font-bold text-[#0F172A]"
                                                        data-testid={`compare-${currentSlug}-col-current`}
                                                    >
                                                        {meta.short}
                                                    </span>
                                                ) : (
                                                    <Link
                                                        to={pillarHref(slug)}
                                                        data-testid={`compare-${currentSlug}-col-${slug}`}
                                                        className="group inline-flex items-center gap-1 font-heading text-base sm:text-lg font-bold text-[#1E3A8A] hover:text-[#0F172A] hover:underline underline-offset-4"
                                                    >
                                                        {meta.short}
                                                        <ArrowRight className="w-3.5 h-3.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                                                    </Link>
                                                )}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, ri) => (
                                <tr
                                    key={row.label}
                                    className={ri % 2 === 1 ? "bg-[#F8FAFC]" : "bg-white"}
                                >
                                    <th
                                        scope="row"
                                        className="p-4 align-top text-sm font-heading font-semibold text-[#0F172A] border-b border-slate-100"
                                    >
                                        {row.label}
                                    </th>
                                    {columns.map((slug) => {
                                        const isCurrent = slug === currentSlug;
                                        return (
                                            <td
                                                key={slug}
                                                className={
                                                    isCurrent
                                                        ? "p-4 align-top text-sm text-[#0F172A] leading-relaxed border-b border-amber-100 bg-amber-50/60"
                                                        : "p-4 align-top text-sm text-slate-700 leading-relaxed border-b border-slate-100"
                                                }
                                                data-testid={`compare-${currentSlug}-cell-${slug}-${ri}`}
                                            >
                                                {row.cells[slug] || "—"}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* -------------------------------------------------------- */}
                {/* Mobile: stacked cards per pillar                         */}
                {/* -------------------------------------------------------- */}
                <div className="md:hidden space-y-4" data-speakable="true">
                    {columns.map((slug) => {
                        const meta = COMPARISON_PILLAR_META[slug];
                        const isCurrent = slug === currentSlug;
                        return (
                            <div
                                key={slug}
                                className={
                                    isCurrent
                                        ? "rounded-2xl border-2 border-amber-400 bg-amber-50 p-5"
                                        : "rounded-2xl border border-slate-200 bg-white p-5"
                                }
                                data-testid={`compare-${currentSlug}-card-${slug}`}
                            >
                                <div className="flex items-center justify-between mb-3">
                                    {isCurrent ? (
                                        <span className="inline-block px-2 py-0.5 rounded-full bg-amber-500 text-[9px] font-bold uppercase tracking-[0.18em] text-white">
                                            You are here
                                        </span>
                                    ) : (
                                        <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">
                                            Compare
                                        </span>
                                    )}
                                    {isCurrent ? (
                                        <span className="font-heading text-base font-bold text-[#0F172A]">
                                            {meta.short}
                                        </span>
                                    ) : (
                                        <Link
                                            to={pillarHref(slug)}
                                            className="inline-flex items-center gap-1 font-heading text-base font-bold text-[#1E3A8A] hover:underline"
                                        >
                                            {meta.short} <ArrowRight className="w-3.5 h-3.5" />
                                        </Link>
                                    )}
                                </div>
                                <dl className="space-y-3">
                                    {rows.map((row) => (
                                        <div key={row.label}>
                                            <dt className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-0.5">
                                                {row.label}
                                            </dt>
                                            <dd className="text-sm text-slate-700 leading-relaxed">
                                                {row.cells[slug] || "—"}
                                            </dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        );
                    })}
                </div>

                <p className="mt-6 text-xs text-slate-500 max-w-3xl leading-relaxed">
                    Comparison written to be neutral and examiner-accurate — updated Feb-2026 against the latest CBSE, CISCE, IB, Cambridge International, College Board and NTA syllabus documentation. Tap any pillar name to see the full syllabus, tutor panel, price band and free-demo booking.
                </p>
            </div>
        </section>
    );
}
